/**
 * Formatting helpers.
 *
 * Dates are stored as 'YYYY-MM-DD' and formatted through Intl in the active
 * locale, so a French coach reads 27/09/2026 and an Arabic one reads the same
 * instant in their own numerals without the stored value ever changing.
 */

import { ageFromBirthDate, birthDatePrecision } from '@shared/birthdate';

export function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

export function formatDateRange(
  startIso: string,
  endIso: string,
  locale: string,
): string {
  return `${formatDate(startIso, locale)} – ${formatDate(endIso, locale)}`;
}

/**
 * Age on a reference date, as the range the stored precision actually supports.
 *
 * Youth football is age-capped, so this number decides eligibility — which is
 * exactly why a birth date known only to the year must read as "18–19" and not
 * as a clean 18. See shared/birthdate.ts for why partial dates are stored at all.
 */
export function ageOn(
  dateOfBirth: string | null | undefined,
  on: Date = new Date(),
): number | null {
  return ageFromBirthDate(dateOfBirth, on)?.years ?? null;
}

/** '18', or '18–19' when the birth date cannot pin it down. */
export function ageLabel(
  dateOfBirth: string | null | undefined,
  on: Date = new Date(),
): string | null {
  const age = ageFromBirthDate(dateOfBirth, on);
  if (!age) return null;
  return age.approximate ? `${age.years}–${age.maxYears}` : String(age.years);
}

/**
 * A birth date shown at the precision it was recorded with: a full date, a month
 * and year, or a bare year. Never padded out to look more certain than it is.
 */
export function formatBirthDate(
  dateOfBirth: string | null | undefined,
  locale: string,
): string {
  if (!dateOfBirth) return '—';
  const precision = birthDatePrecision(dateOfBirth);
  if (precision === null) return dateOfBirth;
  if (precision === 'year') return dateOfBirth;
  const d = new Date(`${precision === 'month' ? `${dateOfBirth}-01` : dateOfBirth}T00:00:00Z`);
  return new Intl.DateTimeFormat(locale, {
    day: precision === 'day' ? 'numeric' : undefined,
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

export function birthYear(dateOfBirth: string | null | undefined): string {
  return dateOfBirth ? dateOfBirth.slice(0, 4) : '—';
}

/** Team-sheet convention: given name as written, family name in capitals. */
export function sheetName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName.toLocaleUpperCase()}`;
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toLocaleUpperCase();
}
